/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { BridgeService } from '../bridge/bridge.service';
import { Logger, LoggingService } from '../logging/logging.service';

@Injectable({ providedIn: 'root' })
export class CustomHttpService {
    private readonly logger: Logger;
    private requestId: number = 0;

    constructor(
        private loggingService: LoggingService,
        private bridgeService: BridgeService
    ) {
        this.logger = this.loggingService.getLogger('CustomHttpService');
    }

    getRequestId(): number {
        if (this.requestId === Number.MAX_SAFE_INTEGER) {
            this.logger.error('Max request id achieved!!!');
            this.requestId = 0;
        }
        this.requestId++;
        return this.requestId;
    }

    /**
     * The following bridge command provides a way to make http requests
     * It has the following limitations - https://docs.google.com/document/d/14Eu3Kr8fWZ-fEWyCwYT4yE49w4CJjr8k4p9sZs45x2o/edit?usp=sharing
     * Limited encoding types - gzip and br on android native
     * Support for “application/json” and “text/plain” content types for now
     * No support for accept-language header
     * No support for keep-alive header
     * No support for mode
     * No support for etags
     * No support for HTTP methods OPTIONS and HEAD
     * Any implicit headers added by the browser must be explicitly passed as part of headers to this command
     * Backoff and retries are controlled from the component invoking ths API
     *
     * If the above support is needed for a category of http requests, then the implementations would need to be extended
     */
    httpRequest(requestParams: Types.Common.Httprequest.RequestParams): Observable<Types.Common.Httprequest.Response> {
        return this.bridgeService.executeCommand(Commands.Common.Httprequest.HttpRequest, requestParams).pipe(
            take(1),
            tap(
                _ => {},
                e => {
                    this.logger.error('httpRquest failed with error', e);
                }
            )
        );
    }

    cancelHttpRequest(requestParams: Types.Common.Httprequest.CancelHttpRequestParameters): Observable<boolean> {
        return this.bridgeService.executeCommand(Commands.Common.Httprequest.CancelHttpRequest, requestParams).pipe(
            take(1),
            tap(
                _ => {},
                e => {
                    this.logger.error('httpRquest failed with error', e);
                }
            )
        );
    }
}
